import { httpRequest } from './http.js'

/**
 * Réglages du diaporama d'infoservice et playlist. Miroir de
 * `routes/infoservice-slideshow.routes.ts` ; le plan d'une plage est calculé
 * côté serveur (`lib/infoservice-slideshow-plan.ts`), jamais recopié ici.
 */

export type InfoserviceMode = 'VIDEO_FILE' | 'SLIDESHOW'
export type CasparTransitionType = 'CUT' | 'MIX' | 'WIPE' | 'PUSH' | 'SLIDE'
export type TransitionDirection = 'LEFT' | 'RIGHT'
/** Courbe du mouvement, parmi celles que CasparCG sait jouer (`lib/transitionEasing.ts`). */
export type TransitionEasing =
  | 'LINEAR'
  | 'IN_SINE' | 'OUT_SINE' | 'IN_OUT_SINE'
  | 'IN_CUBIC' | 'OUT_CUBIC' | 'IN_OUT_CUBIC'
  | 'IN_EXPO' | 'OUT_EXPO' | 'IN_OUT_EXPO'

export type SlideshowTransition =
  | { kind: 'CASPAR'; type: CasparTransitionType; durationMs: number; direction: TransitionDirection; easing: TransitionEasing }
  | { kind: 'STINGER'; filePath: string; durationMs: number; cutMs: number }

export interface SlideshowConfig {
  mode: InfoserviceMode
  displaySec: number
  holdThresholdSec: number
  introFilePath: string | null
  introDurationMs: number | null
  outroFilePath: string | null
  outroDurationMs: number | null
  introTransition: SlideshowTransition
  announcementTransition: SlideshowTransition
  musicEnabled: boolean
  musicFadeInSec: number
  musicFadeOutSec: number
  cgHiddenDuringIntro: boolean
}

export interface MusicTrack {
  id: string
  position: number
  title: string
  filePath: string
  sourcePath: string
  durationMs: number
  loudnessLufs: number | null
  normalized: boolean
}

export interface SlideshowSettings {
  channelId: string
  config: SlideshowConfig
  activationProblems: string[]
  intro: { fileName: string | null; durationMs: number | null }
  outro: { fileName: string | null; durationMs: number | null }
  music: MusicTrack[]
  musicTotalMs: number
}

export type SlideshowStep =
  | { kind: 'INTRO'; startMs: number; endMs: number }
  | { kind: 'ANNOUNCEMENT'; announcementId: string; tour: number; priority: boolean; startMs: number; endMs: number; prolonged: boolean; cut: boolean }
  | { kind: 'OUTRO'; startMs: number; endMs: number }

export interface SlideshowPlan {
  status: 'SLIDESHOW' | 'NO_ANNOUNCEMENT' | 'TOO_SHORT'
  durationMs: number
  seed: number
  steps: SlideshowStep[]
  music: { startMs: number; fadeInMs: number; fadeOutStartMs: number; endMs: number } | null
  cgVisibleFromMs: number
  tours: number
}

export interface SlideshowPreview {
  plage: { source: 'GRID' | 'SAMPLE'; startAt: string | null; endAt: string | null; durationMs: number }
  day: string
  plan: SlideshowPlan
  announcements: Record<string, { organizationLabel: string; eventLabel: string; priority: boolean }>
  counts: { onAir: number; priorities: number; scheduled: number }
  hasMusic: boolean
}

export type SlideshowVideoSlot = 'intro' | 'outro' | 'introStinger' | 'announcementStinger'

export type SlideshowConfigPatch = Partial<Omit<SlideshowConfig, 'introFilePath' | 'introDurationMs' | 'outroFilePath' | 'outroDurationMs'>>

const BASE = '/infoservice-slideshow'

function json(method: string, body?: unknown): RequestInit {
  return { method, body: body === undefined ? undefined : JSON.stringify(body) }
}

export const slideshowApi = {
  get: (channelId: string) => httpRequest<SlideshowSettings>(`${BASE}/${channelId}`, { cache: 'no-store' }),
  update: (channelId: string, patch: SlideshowConfigPatch) => httpRequest<SlideshowSettings>(`${BASE}/${channelId}`, json('PATCH', patch)),
  setVideo: (channelId: string, slot: SlideshowVideoSlot, filePath: string | null) =>
    httpRequest<{ settings: SlideshowSettings; warning: string | null }>(`${BASE}/${channelId}/video`, json('PUT', { slot, filePath })),
  preview: (channelId: string, params: { seed?: number } = {}) =>
    httpRequest<SlideshowPreview>(`${BASE}/${channelId}/preview${params.seed !== undefined ? `?seed=${params.seed}` : ''}`, { cache: 'no-store' }),
  uploadTrack: (channelId: string, file: File) => {
    const form = new FormData()
    form.append('file', file, file.name)
    return httpRequest<MusicTrack>(`${BASE}/${channelId}/music`, { method: 'POST', body: form })
  },
  reorderTracks: (channelId: string, orderedIds: string[]) => httpRequest<MusicTrack[]>(`${BASE}/${channelId}/music/order`, json('PUT', { orderedIds })),
  removeTrack: (channelId: string, trackId: string) => httpRequest<MusicTrack[]>(`${BASE}/${channelId}/music/${trackId}`, { method: 'DELETE' }),
  /** Reprend un morceau déjà sur le disque (chemin absolu rendu par le sélecteur de fichiers). */
  linkTrack: (channelId: string, path: string) => httpRequest<MusicTrack>(`${BASE}/${channelId}/music/link`, json('POST', { path })),
}
